import { LockOutlined, ReloadOutlined, SafetyCertificateOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, Space, Spin, Typography, message } from 'antd';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

export function LoginPage() {
  const navigate = useNavigate();
  const setSession = useAuth((state) => state.setSession);
  const [errorText, setErrorText] = useState('');
  const [notice, noticeContext] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captcha, setCaptcha] = useState<{ captchaId: string; image: string }>();
  const [form] = Form.useForm();

  const loadCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    try { setCaptcha((await api.get('/auth/captcha')).data); }
    catch {
      setCaptcha(undefined);
      void notice.error('验证码加载失败，请点击验证码区域重试');
    } finally { setCaptchaLoading(false); }
  }, [notice]);

  useEffect(() => { void loadCaptcha(); }, [loadCaptcha]);

  const submit = async (values: { username: string; password: string; organizationCode: string; captchaCode: string }) => {
    if (!captcha) { void message.warning('验证码尚未加载完成'); return; }
    setErrorText('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { ...values, captchaId: captcha.captchaId });
      setSession(data.accessToken, data.user, data.organization.name);
      void message.success('登录成功');
      navigate('/dashboard');
    } catch (error) {
      const detail = isAxiosError(error) ? error.response?.data?.message : undefined;
      const text = isAxiosError(error) && !error.response
        ? '无法连接服务器，请检查网络后重试'
        : isAxiosError(error) && error.response?.status === 400 && values.password.length < 6
          ? '用户名或密码错误（密码至少为6位）'
          : isAxiosError(error) && error.response?.status && error.response.status >= 500
            ? '服务器暂时不可用，请稍后重试'
            : Array.isArray(detail) ? detail.join('；') : typeof detail === 'string' ? detail : '登录失败，请稍后重试';
      setErrorText(text);
      void notice.error(text);
      form.setFieldValue('captchaCode', '');
      await loadCaptcha();
    } finally { setLoading(false); }
  };

  return <div className="login-shell">
    {noticeContext}
    <section className="login-visual">
      <Space size={12}><span className="brand-mark">筑</span><Typography.Title level={3} style={{ color: '#fff', margin: 0 }}>筑衡智管</Typography.Title></Space>
      <div style={{ position: 'relative', zIndex: 1, marginTop: '22vh', maxWidth: 600 }}>
        <Typography.Title style={{ color: '#fff', fontSize: 46, lineHeight: 1.2 }}>让每一个工程决定<br />都有依据、有协同、有追溯</Typography.Title>
        <Typography.Paragraph style={{ color: '#cddafb', fontSize: 17, lineHeight: 1.9 }}>覆盖招标代理与工程监理全流程，以项目数据为核心，以权限审批为边界，让 AI 成为可靠的业务助手。</Typography.Paragraph>
        <Space size={28} style={{ marginTop: 26, color: '#d9e5ff' }}><span>✓ 全流程项目管理</span><span>✓ 细粒度权限</span><span>✓ AI来源追溯</span></Space>
      </div>
    </section>
    <section style={{ display: 'grid', placeItems: 'center', padding: 32 }}>
      <div className="login-card">
        <Typography.Title level={2}>欢迎回来</Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 30 }}>登录您的组织工作空间</Typography.Paragraph>
        {errorText && <Alert type="error" showIcon message={errorText} style={{ marginBottom: 16 }} />}
        <Form form={form} layout="vertical" size="large" onFinish={submit} requiredMark={false} autoComplete="off">
          <Form.Item label="组织代码" name="organizationCode" rules={[{ required: true }]}><Input prefix={<SafetyCertificateOutlined />} placeholder="请输入组织代码" autoComplete="off" /></Form.Item>
          <Form.Item label="用户名" name="username" rules={[{ required: true }]}><Input prefix={<UserOutlined />} placeholder="请输入用户名" autoComplete="off" /></Form.Item>
          <Form.Item label="密码" name="password" rules={[{ required: true }]}><Input.Password prefix={<LockOutlined />} placeholder="请输入密码" autoComplete="new-password" /></Form.Item>
          <Form.Item label="验证码" required>
            <div className="login-captcha-row"><Form.Item name="captchaCode" noStyle normalize={(value) => String(value ?? '').toUpperCase()} rules={[{ required: true, message: '请输入验证码' }, { len: 4, message: '验证码为4位字符' }]}><Input maxLength={4} placeholder="请输入右侧字符" autoComplete="off" /></Form.Item><button type="button" className="login-captcha-image" onClick={() => void loadCaptcha()} aria-label="刷新验证码">{captchaLoading ? <Spin size="small" /> : captcha ? <img src={captcha.image} alt="登录验证码" /> : <ReloadOutlined />}</button></div>
          </Form.Item>
          <Button block type="primary" htmlType="submit" loading={loading} style={{ height: 46, marginTop: 8 }}>进入工作台</Button>
        </Form>
        <div style={{ marginTop: 22, padding: 13, borderRadius: 9, background: '#f5f8fd', color: '#74809a', fontSize: 12 }}>请输入部署时初始化的组织代码和管理员账号；验证码不区分大小写。</div>
      </div>
    </section>
  </div>;
}
